import React, { useMemo } from "react";
import { Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { get } from "lodash";
import Collapse from "./Collapse";
import { getOptionLabel, getOptionValue } from "common/enums";

const mapBooleanToYesNo = (value) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return "-";
};

const UtilityAddressView = ({
  utilityAddress,
  isExpandable = true,
  t,
  utilities,
}) => {
  const isDcsUtility = useMemo(
    () => utilities[0] === getOptionValue("utilityOptions", "DCS"),
    [utilities]
  );

  const fields = [
    {
      name: "Block",
      key: "block.name",
      isRequired: true,
    },
    {
      name: "Building",
      key: "building.name",
      isRequired: true,
    },
    {
      name: "City",
      key: "city.name",
      isRequired: true,
    },
    {
      name: "Floors",
      key: "floors",
      isRequired: false,
      renderField: (floors) =>
        floors.length ? floors.map(({ name }) => name).join(", ") : "N/A",
    },
    {
      name: "Nearest Landmark",
      key: "nearestLandmark",
      isRequired: false,
    },
    {
      name: "Pincode",
      key: "pinCode",
      isRequired: true,
    },
    {
      name: "Premise Area",
      key: "premiseArea.name",
      isRequired: true,
    },
    {
      name: "Premise Type",
      key: "premiseType.name",
      isRequired: true,
    },
    {
      name: "DCS Domestic Premise Type",
      key: "dcsDomesticPremiseType",
      isRequired: true,
      isVisible: isDcsUtility,
      renderField: (value) =>
        getOptionLabel("dcsConnectionTypeEnum", value) || "N/A",
    },
    {
      name: "DCS Individual Premise Type",
      key: "dcsIndividualPremiseType",
      isRequired: true,
      isVisible: isDcsUtility,
      renderField: (value) =>
        getOptionLabel("dcsIndividualUnitTypeEnum", value) || "N/A",
    },
    {
      name: "Premise Usage Purpose",
      key: "premiseUsagePurpose",
      isRequired: true,
    },
    {
      name: "Road",
      key: "road.name",
      isRequired: false,
    },
    {
      name: "Unit Name",
      key: "unitName",
      isRequired: true,
    },
    {
      name: "Zone",
      key: "zone.name",
      isRequired: true,
    },
    {
      name: "BPL",
      key: "isBPL",
      isRequired: true,
      isVisible: utilities.some(
        (utilityId) => getOptionValue("utilityOptions", "Power") === utilityId
      ),
      renderField: (isBPL) => mapBooleanToYesNo(isBPL),
    },
  ];

  return (
    <Collapse title="Utility Address" isExpandable={isExpandable}>
      <Row className="d-flex mb-2">
        {fields.map(({ isVisible, name, key, isRequired, renderField }) => {
          const value = get(utilityAddress, key, "");
          if (typeof isVisible !== "boolean" || isVisible) {
            return (
              <Col key={key} lg={6} className="mb-1">
                <Row>
                  <Col md={5}>{t(name)}:</Col>
                  <Col
                    md={7}
                    className="fw-semibold"
                    style={{ wordBreak: "break-word" }}
                  >
                    {renderField
                      ? renderField(value)
                      : get(utilityAddress, key, null) ??
                        (isRequired ? "-" : "N/A")}
                  </Col>
                </Row>
              </Col>
            );
          }
        })}
      </Row>
    </Collapse>
  );
};

UtilityAddressView.propTypes = {
  utilityAddress: PropTypes.object,
  isExpandable: PropTypes.bool,
  t: PropTypes.func,
  utilities: PropTypes.array,
};

export default withTranslation()(UtilityAddressView);
